import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Subheading } from "../../../design/heading";
import { Text } from "../../../design/text";
import { Select } from "../../../design/select";
import { Input } from "../../../design/input";
import { ErrorMessage, Field, Fieldset, Label } from "../../../design/fieldset";
import { useMatter } from "../../../contexts/MatterContext";
import { visitationScheduleIntervals } from "../../../constants/visitationScheduleIntervals";
import { generateRotationBlocks } from "../../../helpers/generateRotationBlocks";
import { RotationDayEditor } from "../../RotationDayEditor";
import { VisitationScheduleForm } from "../../../models/Forms/VisitationScheduleForm";
import { VisitationSchedule } from "../../../models/VisitationSchedule";
import { VisitationScheduleInterval } from "../../../models/VisitationScheduleInterval";
import { Switch, SwitchField } from "../../../design/switch";
import { visitationScheduleTypes } from "../../../constants/visitationScheduleTypes";

interface EditVisitationRotationStepProps {
  form: VisitationScheduleForm;
  setForm: React.Dispatch<React.SetStateAction<VisitationScheduleForm>>;
  onValidationChange?: (isValid: boolean) => void;
  errors?: any;
}

export const EditVisitationRotationStep: React.FC<
  EditVisitationRotationStepProps
> = ({ form, setForm, onValidationChange, errors }) => {
  const { rotation } = form;
  const { partiesInvolved } = useMatter();
  const [interval, setInterval] = useState<VisitationScheduleInterval>(
    form.interval,
  );
  const [flip, setFlip] = useState(false);

  const supportedIntervals =
    visitationScheduleTypes[form.type!].supportedIntervals;

  const hasMissingTransitionTimes = useMemo(() => {
    const entries = Object.entries(rotation ?? {});
    return entries.some(([indexStr, block], i) => {
      const index = Number(indexStr);
      const currentParticipant = block?.participantId;
      const prevBlock = rotation![index - 1];
      const prevParticipant = prevBlock?.participantId;
      const transitionRequired =
        index === 0 ||
        (currentParticipant &&
          prevParticipant &&
          currentParticipant !== prevParticipant);

      return transitionRequired && !block.time;
    });
  }, [rotation]);

  useEffect(() => {
    const newRotation = generateRotationBlocks(
      {},
      form.type!,
      partiesInvolved,
      interval,
      flip,
    );
    setForm((prev) => ({
      ...prev,
      rotation: newRotation,
    }));
  }, [form.startDate, partiesInvolved, interval, flip]);

  useEffect(() => {
    onValidationChange?.(!hasMissingTransitionTimes);
  }, [hasMissingTransitionTimes]);

  const handleBlockUpdate = (
    dayOffset: number,
    updatedBlock: VisitationSchedule["rotation"][number],
  ) => {
    setForm((prev) => ({
      ...prev,
      rotation: {
        ...prev.rotation,
        [dayOffset]: updatedBlock,
      },
    }));
  };

  return (
    <div className="space-y-6">
      <Fieldset>
        <Field>
          <Label htmlFor="name">Schedule Name</Label>
          <Input
            id="name"
            type="text"
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            placeholder="Enter schedule name"
          />
          {errors?.name && <ErrorMessage>{errors.name}</ErrorMessage>}
        </Field>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label htmlFor="interval">Rotation Interval</Label>
            <Select
              id="interval"
              value={interval}
              onChange={(e) =>
                setInterval(e.target.value as VisitationScheduleInterval)
              }
            >
              {supportedIntervals.map((interval) => (
                <option key={interval} value={interval}>
                  {visitationScheduleIntervals[interval].title}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={moment(form.startDate).format("YYYY-MM-DD")}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  startDate: moment(e.target.value).format("YYYY-MM-DD"),
                }))
              }
            />
          </Field>
        </div>
      </Fieldset>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Subheading>Rotation Blocks</Subheading>

        <SwitchField>
          <Label>Switch Parents</Label>
          <Switch checked={flip} onChange={(checked) => setFlip(checked)} />
        </SwitchField>
      </div>

      <div className="space-y-4">
        {Object.entries(rotation ?? {}).map(([indexStr, block], i, arr) => {
          const index = Number(indexStr);
          const currentParticipant = block?.participantId;
          const prevBlock = rotation![index - 1];
          const prevParticipant = prevBlock?.participantId;
          const transitionRequired =
            index === 0 ||
            (currentParticipant &&
              prevParticipant &&
              currentParticipant !== prevParticipant);

          return (
            <RotationDayEditor
              key={index}
              block={block}
              dayOffset={index}
              startDate={form.startDate}
              transitionRequired={!!transitionRequired}
              onChange={(updated) => handleBlockUpdate(index, updated)}
            />
          );
        })}
      </div>
      {hasMissingTransitionTimes && (
        <Text className="text-sm font-medium text-red-600">
          Please provide a transition time for each day custody begins or
          changes.
        </Text>
      )}
    </div>
  );
};
